import React from 'react';
import ReactSelect, { components } from 'react-select';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import _ from 'lodash';

type Props = {
  value: string | number,
  items: any[],
  label?: React.ReactNode,
  onConfirm?: (val: string | number, highlight?: boolean) => void,
  onCancel?: (val: string | number) => void,
  autocomplete?: boolean, // if true, use ReactSelect
  selectFieldProps?: {
    itemValue?: string; // key for value if items are objects
    itemLabel?: string; // key for label if items are objects
    [k: string]: any;
  },
  shouldConfirm: boolean,
  shouldConfirmCb?: (val: string | number) => boolean
  title: string,
  content: string | ((val: string | number) => string),
  disabled?: boolean,
  optional_array_values?: any[],
  error?: boolean,
  hideSelectedOption?: boolean,
  compactConfirm?: boolean,
};

type State = {
  value: string | number,
  tempValue: string | number,
  confirmVisible: boolean,
};

const SELECTED_OPTION_SCROLL_PADDING = 12;

function scrollSelectedOptionFullyIntoView(menuList: HTMLElement | null) {
  if (!menuList) {
    return;
  }
  const selected = menuList.querySelector(
    '.react_select__option--is-selected, .react_select__option--is-focused',
  ) as HTMLElement | null;
  if (!selected) {
    return;
  }

  const extra = SELECTED_OPTION_SCROLL_PADDING;
  if (selected.offsetTop < menuList.scrollTop + extra) {
    menuList.scrollTop = Math.max(0, selected.offsetTop - extra);
  } else if (selected.offsetTop + selected.offsetHeight > menuList.scrollTop + menuList.clientHeight - extra) {
    menuList.scrollTop = selected.offsetTop + selected.offsetHeight - menuList.clientHeight + extra;
  }

  const optionRect = selected.getBoundingClientRect();
  const menuRect = menuList.getBoundingClientRect();
  let visibleTop = menuRect.top;
  let visibleBottom = menuRect.bottom;

  let ancestor: HTMLElement | null = menuList.parentElement;
  while (ancestor) {
    const overflowY = window.getComputedStyle(ancestor).overflowY;
    if (overflowY === 'auto' || overflowY === 'hidden' || overflowY === 'scroll') {
      const rect = ancestor.getBoundingClientRect();
      visibleTop = Math.max(visibleTop, rect.top);
      visibleBottom = Math.min(visibleBottom, rect.bottom);
    }
    ancestor = ancestor.parentElement;
  }

  if (optionRect.top < visibleTop + extra) {
    menuList.scrollTop -= visibleTop + extra - optionRect.top;
  } else if (optionRect.bottom > visibleBottom - extra) {
    menuList.scrollTop += optionRect.bottom - (visibleBottom - extra);
  }
}

function MenuListWithInstantScroll(props: any) {
  const listRef = React.useRef<HTMLDivElement | null>(null);

  const setRef = (node: HTMLDivElement | null) => {
    listRef.current = node;
    const { innerRef } = props;
    if (typeof innerRef === 'function') {
      innerRef(node);
    } else if (innerRef) {
      innerRef.current = node;
    }
  };

  React.useLayoutEffect(() => {
    // Run after react-select's own componentDidUpdate scroll, but still
    // before paint, so the menu opens already at the selected row.
    queueMicrotask(() => {
      scrollSelectedOptionFullyIntoView(listRef.current);
    });
  }, []);

  return <components.MenuList {...props} innerRef={setRef} />;
}

export default class ConfirmationSelectField extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      value: props.value,
      tempValue: props.value,
      confirmVisible: false,
    };
  }

  // Use modern lifecycle to sync state with props
  componentDidUpdate(prevProps: Props) {
    if (prevProps.value !== this.props.value) {
      this.setState({
        value: this.props.value,
        // preserve last committed value as tempValue for cancel
        tempValue: this.state.value,
        confirmVisible: false
      });
    }
  }

  // Helpers to safely get label/value from either primitives or objects
  isPrimitive = (x: any) => (typeof x !== 'object' || x === null);

  getItemValue = (item: any) => {
    const key = this.props.selectFieldProps?.itemValue;
    if (this.isPrimitive(item)) return item as (string | number);
    if (key && item && item[key] != null) return item[key] as (string | number);
    // sensible defaults for objects
    if (item?.id != null) return item.id as (string | number);
    if (item?.value != null) return item.value as (string | number);
    // last resort: stringify index in render map caller (but here return empty)
    return '';
  };

  getItemLabel = (item: any) => {
    const key = this.props.selectFieldProps?.itemLabel;
    if (this.isPrimitive(item)) return String(item);
    if (key && item && item[key] != null) return String(item[key]);
    if (item?.name != null) return String(item.name);
    if (item?.label != null) return String(item.label);
    return String(this.getItemValue(item));
  };

  // MUI reports a string. Keep the option's original value so later
  // comparisons (roof type, clamps, building code) still match.
  resolveStoredValue = (raw: any) => {
    const match = (this.props.items || []).find(
      (item) => String(this.getItemValue(item)) === String(raw),
    );
    return match ? this.getItemValue(match) : raw;
  };

  handleFieldChange = (val: any) => {
    const { shouldConfirm, onConfirm, shouldConfirmCb } = this.props;

    // MUI Select passes primitive, ReactSelect passes the full object.
    const rawValue = this.isPrimitive(val) ? val : (val?.id ?? val?.value ?? val);
    const value = this.resolveStoredValue(rawValue);

    const requiresConfirm = !!shouldConfirm && (!shouldConfirmCb || shouldConfirmCb(value));
    const highlight = !this.isPrimitive(val) ? !!val?.highlight : false;

    if (requiresConfirm && !highlight) {
      this.setState({ value, tempValue: this.state.value, confirmVisible: true });
    } else {
      this.setState({ value, tempValue: this.state.value, confirmVisible: false });
      onConfirm?.(value, highlight);
    }
  };

  confirm = () => {
    const { onConfirm } = this.props;
    this.setState({ tempValue: this.state.value, confirmVisible: false });
    onConfirm?.(this.state.value);
  };

  cancel = () => {
    const { onCancel } = this.props;
    this.setState({ value: this.state.tempValue, confirmVisible: false });
    onCancel?.(this.state.tempValue);
  };

  renderSelect = () => {
    const { items, label, error, selectFieldProps, hideSelectedOption } = this.props;
    const { value } = this.state;
    const {
      itemLabel: _itemLabel,
      itemValue: _itemValue,
      id,
      className: selectClassName,
      fullWidth: _fullWidth,
      ...restSelectFieldProps
    } = selectFieldProps || {};
    const controlClassName = [
      'confirmation-select-field',
      error ? 'confirmation-select-field--error' : '',
      selectClassName,
    ].filter(Boolean).join(' ');

    const externalLabel = typeof label === 'string' && label.length > 0
      ? <div className="input-label">{label}</div>
      : label;

    const selectedItem = (items || []).find(
      (item) => String(this.getItemValue(item)) === String(value),
    );
    const selectedLabel = selectedItem ? this.getItemLabel(selectedItem) : String(value ?? '');

    return (
      <>
        {externalLabel || null}
        <FormControl
          disabled={!!this.props?.disabled}
          error={!!error}
          className={controlClassName}
          id={id ? `${id}-control` : undefined}
          inputProps={{ 'aria-invalid': error || undefined }}
          displayEmpty
        >
          <Select
            id={id}
            value={value == null || value === '' ? '' : String(value)}
            sx={{ minWidth: 0 }}
            variant="standard"
            onChange={(event) => this.handleFieldChange(event.target.value)}
            error={!!error}
            inputProps={{ id, 'aria-invalid': error || undefined }}
            displayEmpty
            MenuProps={{
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
            }}
            {...restSelectFieldProps}
            renderValue={() => (selectedItem ? selectedLabel : '')}
          >
            {hideSelectedOption && value != null && value !== '' && (
              <MenuItem value={String(value)} sx={{ display: 'none' }} aria-hidden>
                {selectedLabel}
              </MenuItem>
            )}
            {(items || []).map((item, index) => {
              const v = this.getItemValue(item);
              if (hideSelectedOption && String(v) === String(value)) {
                return null;
              }
              const text = this.getItemLabel(item);
              return (
                <MenuItem key={`${String(v)}-${index}`} value={String(v)}>
                  {text}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </>
    );
  };

  renderProSelect = () => {
    const { selectFieldProps, items, error, label } = this.props;
    const { value } = this.state;
    const {
      className: selectClassName,
      listHeightRestricted,
      components: selectComponents,
      ...restSelectFieldProps
    } = selectFieldProps || {};
    const controlClassName = [
      'confirmation-select-field',
      error ? 'confirmation-select-field--error' : '',
      selectClassName,
    ].filter(Boolean).join(' ');

    const findByValue = (arr: any[], v: any) =>
      arr.find(it => {
        const itVal =
          this.isPrimitive(it)
            ? it
            : (selectFieldProps?.itemValue
                ? it?.[selectFieldProps.itemValue]
                : (it?.id ?? it?.value));
        return String(itVal) === String(v);
      });

    const valObj = findByValue(items, value) || null;

    if (this.props.optional_array_values && this.props.optional_array_values.length > 0 && this.props.optional_array_values[1] && valObj && !this.isPrimitive(valObj)) {
      if (valObj[selectFieldProps?.itemValue] === 185) {
        valObj.name = this.props.optional_array_values[0];
      } else {
        valObj.name = this.props.optional_array_values[1];
      }
    }

    return (
      <ReactSelect
        value={valObj}
        styles={{
          valueContainer: (base) => ({ ...base, padding: 0 }),
          indicatorSeparator: () => ({ display: 'none' }),
          option: (base, state) => {
            const color = state.isSelected ? '#003D5F' : '#334155';
            const fontWeight = state.data?.highlight ? 700 : 500;
            return {
              ...base,
              fontFamily: '"PT Sans", sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              textAlign: 'left',
              color: 'rgb(51, 51, 51)',
              letterSpacing: "0.03",
              lineHeight: '14px',
            };
          },
          control: (base, state) => {
            const borderColor = error ? '#c5353f' : '#bbbbbb';
            return {
              ...base,
              fontFamily: '"PT Sans", sans-serif',
              border: error ? `1px solid ${borderColor}` : 'none !important',
              fontSize: '14px',
              textAlign: 'left',
              fontWeight: 400,
              borderBottom: `1px solid ${borderColor} !important`,
              borderRadius: error ? '5px' : '0px',
              color: 'rgb(51, 51, 51)',
              lineHeight: '14px',
              letterSpacing: "0.03",
              outline: 'none !important',
              boxShadow: 'none !important',
            };
          },
          menu: (base) => ({
            ...base,
            zIndex: 30,
            backgroundColor: '#FFFFFF',
            border: '1px solid #D6E1E8',
            boxShadow: '0 10px 24px rgba(0, 40, 70, 0.16)',
            overflow: 'hidden',
            transformOrigin: 'left top',
          }),
          menuList: (base) => ({
            ...base,
            backgroundColor: '#FFFFFF',
            padding: '4px 0',
          }),
        }}
        onChange={this.handleFieldChange}
        options={items}
        getOptionLabel={(item: any) => this.getItemLabel(item)}
        getOptionValue={(item: any) => String(this.getItemValue(item))}
        classNamePrefix="react_select"
        className={controlClassName}
        maxMenuHeight={listHeightRestricted ? 250 : undefined}
        {...restSelectFieldProps}
        components={{
          ...selectComponents,
          MenuList: MenuListWithInstantScroll,
        }}
        aria-label={label || undefined}
        aria-invalid={error || undefined}
        isDisabled={!!this.props?.disabled}
      />
    );
  };

  render() {
    const { title, content, autocomplete, compactConfirm = true } = this.props;
    const { confirmVisible, value } = this.state;

    return (
      <>
        {autocomplete ? this.renderProSelect() : this.renderSelect()}

        <Dialog
          id={compactConfirm ? 'arrays-lost-modal' : undefined}
          className={compactConfirm ? 'layout-confirm-dialog' : undefined}
          open={confirmVisible}
          onClose={this.cancel}
          maxWidth={compactConfirm ? false : undefined}
          aria-labelledby="confirmation-dialog-title"
          PaperProps={compactConfirm ? { className: 'layout-confirm-dialog-paper' } : undefined}
        >
          <DialogTitle id="confirmation-dialog-title" className="confirm-clear-arrays-title">
            {title}
          </DialogTitle>
          <DialogContent className="confirm-clear-arrays-content">
            <p>{_.isFunction(content) ? content(value) : content}</p>
          </DialogContent>
          <DialogActions>
            {compactConfirm ? (
              <>
                <Button className="cancel-button" onClick={this.cancel} variant="text">
                  NO, CANCEL
                </Button>
                <Button className="submit-button" onClick={this.confirm} variant="text">
                  YES, CONTINUE
                </Button>
              </>
            ) : (
              <>
                <Button onClick={this.cancel} color="secondary">
                  NO, CANCEL
                </Button>
                <Button onClick={this.confirm} color="primary" variant="contained">
                  YES, CONTINUE
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </>
    );
  }
}
